"use client";
import { commentStyles } from "./forum-comments.styles";
import { layoutStyles } from "../forum-layout.styles";

import * as stylex from "@stylexjs/stylex";
import { Spinner } from "@softmax/design-system";
import { Comment } from "./forum-comment";
import { CommentComposer } from "./forum-comment-composer";
import { RefreshFeedback } from "../forum-refresh-feedback";
import { styles } from "../forum.styles";
import type {
  ForumComposeComment,
  ForumPostState,
  ForumVote,
  ForumCommentVoteHandler,
  ForumMarkdownRenderer,
  ForumLinkRenderer,
  ForumCommentDeletion,
} from "../forum.types";

export function ForumPostComments({
  state,
  commentCount,
  onRefresh,
  commentVotes,
  onCommentVote,
  renderMarkdown,
  renderLink,
  deleteComment,
  compose,
}: Readonly<{
  onRefresh(): void;
  onCommentVote: ForumCommentVoteHandler;
  renderMarkdown: ForumMarkdownRenderer;
  renderLink: ForumLinkRenderer;
  commentVotes: Readonly<Record<string, ForumVote>> | undefined;
  deleteComment: ForumCommentDeletion | undefined;
  compose: ForumComposeComment | undefined;
  state: Extract<ForumPostState, Readonly<{ status: "ready" }>>["comments"];
  commentCount: number | null | undefined;
}>) {
  return (
    <section {...stylex.props(commentStyles.comments)} aria-label="Comments">
      <h2 {...stylex.props(commentStyles.commentsHeading)}>
        Comments{commentCount != null && ` · ${commentCount}`}
      </h2>
      {state.status === "loading" && (
        <output {...stylex.props(styles.loading)}>
          <Spinner />
          Loading comments…
        </output>
      )}
      {state.status === "error" && (
        <RefreshFeedback
          refreshing={false}
          error={state.message}
          onRefresh={onRefresh}
        />
      )}
      {state.status === "ready" && (
        <>
          <RefreshFeedback
            refreshing={state.refreshing}
            error={state.refreshError}
            onRefresh={onRefresh}
          />
          {state.records.length === 0 && (
            <p {...stylex.props(layoutStyles.railDescription)}>
              No comments yet.
            </p>
          )}
          {state.records.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              depth={0}
              votes={commentVotes}
              onVote={onCommentVote}
              renderMarkdown={renderMarkdown}
              renderLink={renderLink}
              deleteComment={deleteComment}
              compose={compose}
            />
          ))}
        </>
      )}
      {compose && (
        <CommentComposer
          parentId={null}
          label="Add a comment"
          compose={compose}
        />
      )}
    </section>
  );
}
